import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { ValidationError } from "@common/validationError";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import { EntityTemplateField, IncidentLogStandardField } from "../../entityTemplates/EntityTemplate.types";
import { IncidentLogCustomFieldRenderer, IncidentLogFieldModel, IncidentLogStandardFieldRenderer } from "../../entityTemplates/IncidentLogFieldRenderers";

export interface IncidentLogTemplateFieldProps {
    field: EntityTemplateField;
    name: string;
    onNameChange: (name: string) => void;
    nameErrorText?: string;
    incidentLog: IncidentLogFieldModel;
    onChange: (changeFunc: (incidentLog: IncidentLogFieldModel) => void) => Promise<void>;
    validationErrors: Array<ValidationError>;
    selectedTagIds: Array<string>;
    onTagsChange: (tagIds: Array<string>) => void;
}

/**
 * One template field, rendered for an incident that has not been created yet. Every branch here replaces a
 * standard renderer that either saves on its own or needs a real incident id. The rest go through the standard
 * renderers untouched.
 */
function IncidentLogTemplateField(props: IncidentLogTemplateFieldProps) {
    const { translateString } = useTranslation();
    const standardField = props.field.standardFieldIdentifier as IncidentLogStandardField | undefined;

    // The standard name field debounces into the incident; the pane keeps the name on its save model so it can be
    // prefilled from an observation and validated before anything else
    if (standardField === IncidentLogStandardField.IncidentLogName) {
        return (
            <DotLegalTextField
                debounce={false}
                size="small"
                label={translateString("incidentName")}
                value={props.name}
                onChange={props.onNameChange}
                errorText={props.nameErrorText}
                autoFocus
            />
        );
    }

    if (standardField === IncidentLogStandardField.IncidentLogTags) {
        return (
            <TagsMultiSelect
                selectedTags={props.selectedTagIds}
                onChange={props.onTagsChange}
                entityType={TagRelationType.IncidentLog}
                isLoading={false}
                errorText={props.validationErrors.find((v) => v.field === IncidentLogStandardField[IncidentLogStandardField.IncidentLogTags])?.error}
            />
        );
    }

    if (standardField) {
        return (
            <IncidentLogStandardFieldRenderer
                field={props.field}
                incidentLog={props.incidentLog}
                incidentLogId=""
                onChange={props.onChange}
                validationErrors={props.validationErrors}
                isLoading={false}
                isPreview={false}
                disableAccessWarning
            />
        );
    }

    return (
        <IncidentLogCustomFieldRenderer
            field={props.field}
            incidentLog={props.incidentLog}
            incidentLogId=""
            onChange={props.onChange}
            validationErrors={props.validationErrors}
            isLoading={false}
            isPreview={false}
        />
    );
}

export default IncidentLogTemplateField;
